import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateModule } from '@ngx-translate/core';
import axios, { AxiosError } from 'axios';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { HelpersService } from 'src/app/services/helpers/helpers.service';
import { environment } from 'src/environments/environment';
import { PayeeButtonComponent } from '../../../../shared/payee-buttons/payee-button/payee-button.component';
import { PayeeMiniIconButtonComponent } from '../../../../shared/payee-buttons/payee-mini-icon-button/payee-mini-icon-button.component';
import { PayeeDropzoneComponent } from '../../../../shared/payee-dropzone/payee-dropzone.component';
import { PayeeAutocompleteComponent } from '../../../../shared/payee-form-fields/payee-autocomplete/payee-autocomplete.component';
import { PayeeFormFieldComponent } from '../../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeIconComponent } from '../../../../shared/payee-icon/payee-icon.component';
import { PayeeInputDirective } from '../../../../shared/payee-input/payee-input.directive';
import { PayeePopupComponent } from '../../../../shared/payee-popup/payee-popup.component';
import { MOKK_DOCUMENT_TYPE_OPTIONS, MokkDocumentTypeOption } from '../mokk-document-types';
import { PayeeCaseMokkRow } from '../payee-case-mokk.types';

type ParentMokkOption = {
  mokk_id: string;
  mokk_label: string | null;
};

@Component({
  selector: 'app-upload-debug-mokk-dialog',
  templateUrl: './upload-debug-mokk-dialog.component.html',
  styleUrls: ['./upload-debug-mokk-dialog.component.scss'],
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    PayeePopupComponent,
    PayeeFormFieldComponent,
    PayeeAutocompleteComponent,
    PayeeDropzoneComponent,
    PayeeButtonComponent,
    PayeeMiniIconButtonComponent,
    PayeeIconComponent,
    PayeeInputDirective,
  ],
})
export class UploadDebugMokkDialogComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private dialogRef = inject<MatDialogRef<UploadDebugMokkDialogComponent, boolean>>(MatDialogRef);
  private helpersService = inject(HelpersService);
  private snackBar = inject(MatSnackBar);
  private readonly destroy$ = new Subject<void>();

  loading = false;
  loadingParentMokks = false;
  file: File | null = null;
  parentMokkOptions: ParentMokkOption[] = [];

  readonly mokkTypeOptions = MOKK_DOCUMENT_TYPE_OPTIONS;

  readonly form = this.fb.group({
    payee_case_reference_id: this.fb.control('', { validators: Validators.required, nonNullable: true }),
    type: this.fb.control<MokkDocumentTypeOption | null>(null, { validators: Validators.required }),
    parent_mokk_id: this.fb.control<ParentMokkOption | null>(null),
  });

  readonly mokkTypeDisplayWith = (option: MokkDocumentTypeOption | null): string => (
    option ? `${option.type} – ${option.label}` : ''
  );

  readonly parentMokkDisplayWith = (option: ParentMokkOption | null): string => {
    if (!option) {
      return '';
    }

    return option.mokk_label
      ? `${option.mokk_id} – ${option.mokk_label}`
      : option.mokk_id;
  };

  ngOnInit(): void {
    this.form.controls.payee_case_reference_id.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(caseRef => {
        this.form.controls.parent_mokk_id.setValue(null);
        void this.loadParentMokkOptions(caseRef);
      });

    this.updateParentMokkControlState();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  filesDropped(files: FileList): void {
    const pdf = Array.from(files).find(file => file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf'));
    this.file = pdf ?? files[0] ?? null;
  }

  removeFile(): void {
    this.file = null;
  }

  async submit(): Promise<void> {
    if (this.form.invalid || this.loading || !this.file) {
      this.helpersService.markAllChildrenAsDirty(this.form);
      if (!this.file) {
        this.snackBar.open('Válasszon PDF fájlt', 'OK', { duration: 3000 });
      }
      return;
    }

    const { payee_case_reference_id, type, parent_mokk_id } = this.form.getRawValue();
    const formData = new FormData();
    formData.append('payee_case_reference_id', payee_case_reference_id.trim());
    formData.append('type', String(type!.type));
    if (parent_mokk_id?.mokk_id) {
      formData.append('parent_mokk_id', parent_mokk_id.mokk_id);
    }
    formData.append('file', this.file);

    try {
      this.loading = true;
      this.updateParentMokkControlState();
      await axios.post(`${environment.baseUrl}/api/payee-case-mokk/debug-upload`, formData);
      this.dialogRef.close(true);
    } catch (error) {
      console.error('Error while uploading debug MOKK', error);
      let errorMessage = 'Nem sikerült feltölteni a teszt MOKK iratot';
      if (error instanceof AxiosError && error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      this.snackBar.open(errorMessage, 'OK', { duration: 10_000 });
    } finally {
      this.loading = false;
      this.updateParentMokkControlState();
    }
  }

  private async loadParentMokkOptions(caseRef: string): Promise<void> {
    const trimmedCaseRef = caseRef.trim();
    if (!trimmedCaseRef) {
      this.parentMokkOptions = [];
      this.updateParentMokkControlState();
      return;
    }

    try {
      this.loadingParentMokks = true;
      this.updateParentMokkControlState();

      const res = await axios.get<{ data: PayeeCaseMokkRow[] }>(
        `${environment.baseUrl}/api/payee-case-mokk`,
        {
          params: {
            payee_case_reference_id: trimmedCaseRef,
            include_without_media: 1,
            include_with_transitions: 1,
            show_hidden: 1,
            per_page: 100,
            page: 1,
          },
        },
      );

      this.parentMokkOptions = res.data.data.map(row => ({
        mokk_id: row.mokk_id,
        mokk_label: row.mokk_label,
      }));
    } catch (error) {
      console.error('Error while loading parent MOKK options', error);
      this.parentMokkOptions = [];
    } finally {
      this.loadingParentMokks = false;
      this.updateParentMokkControlState();
    }
  }

  private updateParentMokkControlState(): void {
    const caseRef = this.form.controls.payee_case_reference_id.value.trim();
    const parentControl = this.form.controls.parent_mokk_id;

    if (!caseRef || this.loading || this.loadingParentMokks) {
      parentControl.disable({ emitEvent: false });
      return;
    }

    parentControl.enable({ emitEvent: false });
  }

  readonly cancel = (): void => {
    this.dialogRef.close();
  };
}
